import axios from "axios";

/**
 * 格式化时间
 * @param {Date|number|string} value  可是 Date、毫秒时间戳、可被 Date 解析的字符串
 * @param {string} fmt                格式模板（默认 yyyy-MM-dd hh:mm:ss）
 * @param {string} [timeZone]         可选时区，如 'Asia/Shanghai'、'UTC'
 * @returns {string}
 */
export function formatTime(value, fmt = 'yyyy-MM-dd hh:mm:ss', timeZone="Asia/Shanghai") {
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date)) return '';

  // 指定时区时，使用 Intl 拿到各部分；否则走本地 getXX
  let parts;
  if (timeZone) {
    const dtf = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    });
    const map = Object.fromEntries(
      dtf.formatToParts(date).map(p => [p.type, p.value])
    );
    parts = {
      yyyy: map.year,
      MM: map.month,
      dd: map.day,
      hh: map.hour,
      mm: map.minute,
      ss: map.second,
    };
  } else {
    const pad = n => String(n).padStart(2, '0');
    parts = {
      yyyy: date.getFullYear(),
      MM: pad(date.getMonth() + 1),
      dd: pad(date.getDate()),
      hh: pad(date.getHours()),
      mm: pad(date.getMinutes()),
      ss: pad(date.getSeconds()),
    };
  }

  return fmt.replace(/yyyy|MM|dd|hh|mm|ss/g, k => parts[k]);
}

export function preferMobileH264(transceiver) {
  if (!transceiver || !RTCRtpSender.getCapabilities) return;
  const caps = RTCRtpSender.getCapabilities('video');
  if (!caps?.codecs) return;

  // 只保留 Baseline (profile-level-id 42xxxx) 且 packetization-mode=1 的 H264
  const mobileH264 = caps.codecs.filter(c => {
    const mime = (c.mimeType || '').toLowerCase();
    const fmtp = (c.sdpFmtpLine || '').toLowerCase();
    const isH264 = mime.includes('h264');
    const isBaseline = /profile-level-id=42/i.test(fmtp);   // 42e01f 等
    const pm1 = /packetization-mode=1/.test(fmtp);
    return isH264 && isBaseline && pm1;
  });

  if (mobileH264.length) {
    try { transceiver.setCodecPreferences(mobileH264); } catch (_) { }
  }
}
export function preferOpus(transceiver) {
  if (!transceiver || !RTCRtpSender.getCapabilities) return;
  const caps = RTCRtpSender.getCapabilities('audio');
  if (!caps?.codecs) return;
  const opusFirst = [
    ...caps.codecs.filter(c => /opus/i.test(c.mimeType || '')),
    ...caps.codecs.filter(c => !/opus/i.test(c.mimeType || '')),
  ];
  try { transceiver.setCodecPreferences(opusFirst); } catch (_) { }
}

export function forceH264Baseline(sdp) {
  const lines = sdp.split('\n');

  // 找到 m=video 段的起止范围（下一个 m=... 之前）
  const vStart = lines.findIndex(l => l.startsWith('m=video'));
  if (vStart === -1) return sdp;
  let vEnd = lines.length;
  for (let i = vStart + 1; i < lines.length; i++) {
    if (lines[i].startsWith('m=')) { vEnd = i; break; }
  }

  // 只在 [vStart, vEnd) 里操作
  const seg = lines.slice(vStart, vEnd);

  // 找出 H.264 的 payload types
  const h264Pts = [];
  seg.forEach(l => {
    const m = l.match(/^a=rtpmap:(\d+)\s+H264\/90000/i);
    if (m) h264Pts.push(m[1]);
  });
  if (!h264Pts.length) return sdp;

  // 选第一个 H.264 pt
  const chosen = h264Pts[0];

  // m=video 只保留 chosen
  seg[0] = seg[0].replace(/^(m=video\s+\d+\s+\S+\s+).*$/, (_, head) => `${head}${chosen}`);

  // 删除 video 段内其它视频编码（VP8/VP9/H265/非 chosen 的 H.264）
  for (let i = seg.length - 1; i >= 0; i--) {
    const l = seg[i];
    // 非 chosen 的 H.264
    const mH = l.match(/^a=rtpmap:(\d+)\s+H264\/90000/i);
    if (mH && mH[1] !== chosen) {
      const pt = mH[1];
      // 删相关 fmtp/rtcp-fb
      for (let j = seg.length - 1; j >= 0; j--) {
        if (seg[j].startsWith(`a=fmtp:${pt}`) || seg[j].startsWith(`a=rtcp-fb:${pt}`)) seg.splice(j, 1);
      }
      seg.splice(i, 1);
      continue;
    }
    // 其它视频编码
    const isOtherVideo = /^a=rtpmap:\d+\s+(VP8|VP9|H265|HEVC|AV1)\/90000/i.test(l);
    if (isOtherVideo) {
      const pt = l.match(/^a=rtpmap:(\d+)/)?.[1];
      if (pt) {
        for (let j = seg.length - 1; j >= 0; j--) {
          if (seg[j].startsWith(`a=fmtp:${pt}`) || seg[j].startsWith(`a=rtcp-fb:${pt}`)) seg.splice(j, 1);
        }
      }
      seg.splice(i, 1);
    }
  }

  // 规范 chosen 的 fmtp（移动端友好）
  let hasFmtp = false;
  for (let i = 0; i < seg.length; i++) {
    if (seg[i].toLowerCase().startsWith(`a=fmtp:${chosen}`)) {
      hasFmtp = true;
      seg[i] = `a=fmtp:${chosen} level-asymmetry-allowed=1;packetization-mode=1;profile-level-id=42e01f`;
    }
  }
  if (!hasFmtp) {
    const idx = seg.findIndex(l => l.match(new RegExp(`^a=rtpmap:${chosen}\\s+H264\\/90000`, 'i')));
    if (idx !== -1) {
      seg.splice(idx + 1, 0, `a=fmtp:${chosen} level-asymmetry-allowed=1;packetization-mode=1;profile-level-id=42e01f`);
    }
  }

  // 写回
  lines.splice(vStart, vEnd - vStart, ...seg);
  return lines.join('\n');
}


export function getQuery() {
  return Object.fromEntries(new URL(location.href).searchParams.entries());
}

export function saveFirstTouch() {
  const q = getQuery();
  const hasUtm = ['utm_source','utm_medium','utm_campaign','utm_content','utm_term']
    .some(k => q[k]);
  if (!localStorage.getItem('first_touch') && hasUtm) {
    localStorage.setItem('first_touch', JSON.stringify({
      ts: Date.now(),
      ...q,
      referrer: document.referrer || ''
    }));
  }
}

export function detectSource() {
  const q = Object.fromEntries(new URL(location.href).searchParams.entries());
  const ref = document.referrer || '';
  const first = JSON.parse(localStorage.getItem('first_touch') || 'null');
  if (!first) {
    // 简单识别平台（可自行扩展规则）
    const sourceGuess =
      q.utm_source ||
      (/google\./i.test(ref) ? 'google' :
       /facebook\.|fb\.com/i.test(ref) ? 'facebook' :
       /t\.co|twitter\.com/i.test(ref) ? 'twitter' :
       /tiktok\.|byteoversea/i.test(ref) ? 'tiktok' :
       ref ? 'referral' : 'direct');

    const payload = {
      ts: Date.now(),
      page: location.pathname + location.search,
      referrer: ref,
      source: sourceGuess,
      utm_source: q.utm_source || '',
      utm_medium: q.utm_medium || '',
      utm_campaign: q.utm_campaign || '',
      fbclid: q.fbclid || '',
      gclid: q.gclid || ''
    };
    localStorage.setItem('first_touch', JSON.stringify(payload));
    // 可选：立即上报首触点
    axios.post('/api/track/first-touch', payload).catch(()=>{});
  }
}


const LIVE_KEEP_KEY = 'lvtu_live_keep';

export function setKeepLive(flag) {
  try { localStorage.setItem(LIVE_KEEP_KEY, JSON.stringify({ keep: !!flag, t: Date.now() })); } catch (_) {}
}
export function getKeepLive() {
  try {
    const obj = JSON.parse(localStorage.getItem(LIVE_KEEP_KEY) || '{}');
    return !!obj.keep;
  } catch (_) { return false; }
}
export function clearKeepLive() {
  try { localStorage.removeItem(LIVE_KEEP_KEY); } catch (_) {}
}